/**
 * カナ正規化ユーティリティ（フロントエンド版）
 * バックエンド版: backend/adonis/app/Utils/NormalizeKanaUtil.js
 * ロジック変更時は両方を同時に更新すること
 */

/**
 * ひらがなを全角カタカナに変換する
 * @param {string} str
 * @returns {string}
 */
function hiraganaToKatakana(str) {
  return str.replace(/[ぁ-ゖ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0x60));
}

/**
 * カナ文字列を正規化する
 * - NFKC正規化（半角カタカナ → 全角カタカナ、全角英数 → 半角英数 等）
 * - ひらがな → 全角カタカナ変換
 * - 前後の空白をトリム
 * - 結果が空文字の場合は null を返す
 *
 * @param {string|null|undefined} value - 入力値
 * @returns {string|null} 正規化後の文字列（空なら null）
 */
export function normalizeKana(value) {
  if (!value) return null;
  let result = value;
  // NFKC正規化: 半角カタカナ → 全角カタカナ、濁点・半濁点の結合等
  result = result.normalize('NFKC');
  // ひらがな → 全角カタカナ
  result = hiraganaToKatakana(result);
  // 前後の空白をトリム
  result = result.trim();
  return result || null;
}

/**
 * 正規化済みカナ文字列が有効かどうかを検証する
 * 全角カタカナ・長音記号・半角スペース・全角スペースのみで構成されていれば true
 * falsy 入力（null/undefined/空文字）は任意項目として true 扱い
 *
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export function isValidKana(value) {
  if (!value) return true;
  return /^[ァ-ヶー 　]+$/u.test(value);
}

/**
 * カナ文字列を正規化したうえで妥当性検証する
 * 入力値を一発で正規化＋検証するヘルパ。
 * - 入力が falsy（null/undefined/空文字）の場合は { normalized: null, isValid: true }
 *   （kana は任意項目のため null/未指定は合法）
 * - それ以外は normalizeKana → isValidKana の結果を返却
 *
 * @param {string|null|undefined} value - 未正規化の入力値
 * @returns {{ normalized: string|null, isValid: boolean }}
 */
export function normalizeAndValidate(value) {
  if (!value) return { normalized: null, isValid: true };
  const normalized = normalizeKana(value);
  return { normalized, isValid: isValidKana(normalized) };
}
